import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, useMediaQuery, useTheme, Paper, Stack, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getInvoiceList, getInvoiceDetail } from "../Model/Auction";




import { C, accountRootSx, eyebrowSx, inputSx, outlineButtonSx, textButtonSx } from "./accountStyles";
import { SectionHeader } from "./components/AccountSection";
import { EmptyState, LoadingState } from "./components/primitives";
import { InvoiceItem, InvoiceItems, InvoiceListHead, InvoiceSummary, ViewToggle } from "./components/InvoiceViews";
import InvoiceReceipt from "./components/InvoiceReceipt";



export default function InvoiceList() {
  const [search, setSearch] = useState("");
  const [invoicesData, setInvoicesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const [totalCount, setTotalCount] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const BELL_MT = "'Bell MT', serif";

  useEffect(() => {
    async function fetchInvoices() {
      setLoading(true);
      try {
        const response = await getInvoiceList(search, "", page * rowsPerPage, rowsPerPage);
        const resData = response?.data;
        if (resData && !Array.isArray(resData)) {
          setInvoicesData([resData]);
          setTotalCount(1);
        } else if (Array.isArray(resData)) {
          setInvoicesData(resData);
          setTotalCount(resData.length < rowsPerPage ? page * rowsPerPage + resData.length : (page + 2) * rowsPerPage);
        } else { setInvoicesData([]); setTotalCount(0); }
      } catch { setInvoicesData([]); } finally { setLoading(false); }
    }
    fetchInvoices();
  }, [search, page, rowsPerPage]);

  const handleSelectInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setTabIndex(1);
    setDetailLoading(true);
    try {
      const res = await getInvoiceDetail(invoice.id);
      if (res?.success) setInvoiceDetail(res);
      else setInvoiceDetail(null);
    } catch { setInvoiceDetail(null); } finally { setDetailLoading(false); }
  };

  const handlePrint = () => { window.print(); };

  const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <Box sx={{ ...accountRootSx, width: "100%" }}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; font-family: 'Bell MT', serif !important; }
            #receipt-content, #receipt-content * { visibility: visible; }
            #receipt-content { position: absolute; left: 0; top: 0; width: 100%; color: black !important; padding: 20px; font-family: 'Bell MT', serif !important; }
            .no-print { display: none !important; }
            img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}
      </style>

      {tabIndex === 0 && (
        <>
          <SectionHeader
            id="invoices-title"
            title="Invoices"
            description="Invoices for the lots you have won. Select an invoice to see its items."
            actions={<ViewToggle viewMode={viewMode} onChange={setViewMode} />}
          />

          <TextField
            fullWidth
            placeholder="Search by auctions"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            inputProps={{ "aria-label": "Search invoices by auction" }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: C.secondary }} /></InputAdornment>,
            }}
            sx={{ ...inputSx, mb: { xs: 3, md: 4 }, maxWidth: { md: 420 }, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], height: 44 } }}
          />

          {loading ? (
            <LoadingState py={8} />
          ) : invoicesData.length === 0 ? (
            <EmptyState title="No invoices found" text={search ? "Try a different auction name." : "Invoices for lots you win will appear here."} />
          ) : viewMode === "grid" ? (
            <Box role="list" sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 3 } }}>
              {invoicesData.map((inv) => (
                <Box role="listitem" key={inv.id} sx={{ display: "flex", minWidth: 0 }}>
                  <InvoiceItem invoice={inv} viewMode="grid" onOpen={() => handleSelectInvoice(inv)} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box>
              <InvoiceListHead />
              <Box role="list" sx={{ borderTop: { xs: `1px solid ${C.ink}`, md: "none" } }}>
                {invoicesData.map((inv) => (
                  <Box role="listitem" key={inv.id}>
                    <InvoiceItem invoice={inv} viewMode="list" onOpen={() => handleSelectInvoice(inv)} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box component="nav" aria-label="Invoice pages" sx={{ mt: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <Button size="small" disabled={page === 0} onClick={() => setPage(page - 1)} sx={outlineButtonSx}>Prev</Button>
            <Typography sx={{ ...eyebrowSx, minWidth: 64, textAlign: "center" }}>{page + 1} / {pageCount}</Typography>
            <Button size="small" disabled={invoicesData.length < rowsPerPage} onClick={() => setPage(page + 1)} sx={outlineButtonSx}>Next</Button>
          </Box>
        </>
      )}

      {tabIndex === 1 && selectedInvoice && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, md: 4 }, pb: 2, borderBottom: `1px solid ${C.ink}` }} className="no-print">
            <Button
              onClick={() => setTabIndex(0)}
              startIcon={<ArrowBackIcon fontSize="small" />}
              aria-label="Back to invoices"
              sx={{ ...textButtonSx, px: 0 }}
            >
              {isMobile ? "Back" : "Back to Invoices"}
            </Button>

            <Stack direction="row" spacing={1} alignItems="center">
              {selectedInvoice?.status === "PAID" && (
                <>
                  {isMobile ? (
                    <IconButton onClick={handlePrint} aria-label="Print receipt" sx={{ border: `1px solid ${C.ink}`, borderRadius: 0, width: 40, height: 40, color: C.ink }}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Button variant="outlined" size="small" startIcon={<PrintIcon fontSize="small" />} onClick={handlePrint} sx={{ ...outlineButtonSx, minHeight: 40 }}>Print Receipt</Button>
                  )}
                </>
              )}
              <ViewToggle viewMode={viewMode} onChange={setViewMode} />
            </Stack>
          </Stack>

          {detailLoading ? (
            <LoadingState py={8} />
          ) : !invoiceDetail ? (
            <Box className="no-print">
              <EmptyState title="Invoice unavailable" text="The details of this invoice could not be loaded. Please try again later." />
            </Box>
          ) : invoiceDetail && (
            <>
              <Paper elevation={0} sx={{ p: 0, borderRadius: 0, bgcolor: 'transparent' }} className="no-print">
                <InvoiceSummary invoiceDetail={invoiceDetail} selectedInvoice={selectedInvoice} />

                <SectionHeader as="h3" title="Purchased Items" count={invoiceDetail.lot?.length} />
                <InvoiceItems lots={invoiceDetail.lot} viewMode={viewMode} total={invoiceDetail.data.total_amount} />

                
              </Paper>

              
              <InvoiceReceipt invoiceDetail={invoiceDetail} BELL_MT={BELL_MT} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
